import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useAction } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is an npm package, not a local convex/ directory file
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/ are one level deeper than src/pages/, requiring three levels up to reach convex/_generated/
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: convex _generated is the documented exception
import { useOrgContext } from '@/hooks/useOrgContext';
import { useCompanyListDialogs } from '@/hooks/useCompanyListDialogs';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { useTableSort } from '@/hooks/useTableSort';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ColumnSettingsPopover } from '@/components/insurance/ColumnSettingsPopover';
import { SortableColumnHeader } from '@/components/insurance/SortableColumnHeader';
import { COMPANIES_COLUMNS } from '@/lib/tableColumns';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import { Building2, Plus, Archive, ArchiveRestore, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { InviteCompanyDialog } from '@/components/company/InviteCompanyDialog';
import { DeleteCompanyDialog } from '@/components/company/DeleteCompanyDialog';
import type { ICompanyListItem } from '@/@types/company';

function CompanyTableSkeleton() {
  return (
    <>
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex items-center px-6 py-3 border-b border-table-row-border gap-4">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-4 w-44 ml-4" />
          <Skeleton className="h-4 w-16 ml-4" />
          <Skeleton className="h-4 w-16 ml-4" />
          <Skeleton className="h-4 w-20 ml-4" />
          <Skeleton className="h-4 w-24 ml-4" />
          <Skeleton className="h-4 w-20 ml-auto" />
        </div>
      ))}
    </>
  );
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const statusStyles: Record<string, string> = {
  pending:
    'bg-amber-50 text-amber-700 ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-400/20',
  active:
    'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-400/20',
  archived:
    'bg-zinc-50 text-zinc-600 ring-zinc-500/20 dark:bg-zinc-500/10 dark:text-zinc-400 dark:ring-zinc-400/20',
};

type CompanyCellHandlers = {
  // LOCAL_TYPE_JUSTIFIED: narrow handler bundle used only in renderCompanyCell helper; no shared type needed across files
  isOrgOwner: boolean;
  onArchive: (e: React.MouseEvent, id: Id<'companies'>) => void;
  onRestore: (e: React.MouseEvent, id: Id<'companies'>) => void;
  onDelete: (e: React.MouseEvent, id: Id<'companies'>, name: string) => void;
};

function renderCompanyCell(
  colId: string,
  company: ICompanyListItem,
  handlers: CompanyCellHandlers
): React.ReactNode {
  const { isOrgOwner, onArchive, onRestore, onDelete } = handlers;

  switch (colId) {
    case 'name':
      return (
        <span className="text-sm font-medium text-foreground truncate pr-3">{company.name}</span>
      );
    case 'email':
      return (
        <span className="text-sm text-muted-foreground truncate pr-3">{company.email ?? '—'}</span>
      );
    case 'employees':
      return <span className="text-sm text-muted-foreground">{company.employeeCount}</span>;
    case 'members':
      return <span className="text-sm text-muted-foreground">{company.memberCount}</span>;
    case 'status':
      return (
        <span
          className={cn(
            'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset',
            statusStyles[company.status] ?? statusStyles['active']
          )}
        >
          {company.status.charAt(0).toUpperCase() + company.status.slice(1)}
        </span>
      );
    case 'created':
      return <span className="text-sm text-muted-foreground">{formatDate(company.createdAt)}</span>;
    case 'actions':
      if (!isOrgOwner) return null;
      return (
        <div className="flex justify-end gap-0.5">
          {company.status === 'active' && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  onClick={e => onArchive(e, company._id)}
                  aria-label={`Archive ${company.name}`}
                >
                  <Archive className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Archive</TooltipContent>
            </Tooltip>
          )}
          {company.status === 'archived' && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  onClick={e => onRestore(e, company._id)}
                  aria-label={`Restore ${company.name}`}
                >
                  <ArchiveRestore className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Restore</TooltipContent>
            </Tooltip>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={e => onDelete(e, company._id, company.name)}
                aria-label={`Delete ${company.name}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Delete</TooltipContent>
          </Tooltip>
        </div>
      );
    default:
      return null;
  }
}

export function CompaniesListPage() {
  const navigate = useNavigate();
  const { currentOrg, isLoading: orgLoading, isOrgOwner } = useOrgContext();
  const [searchQuery, setSearchQuery] = useState('');
  const { isInviteOpen, setIsInviteOpen, openInvite, deleteTarget, openDelete, closeDelete } =
    useCompanyListDialogs();

  const rawCompanies = useQuery(
    api['companies/queries'].list,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );
  const archiveCompany = useMutation(api['companies/mutations'].archive);
  const restoreCompany = useMutation(api['companies/mutations'].restore);
  const deleteCompany = useAction(api['companies/actions'].verifyPasswordAndDeleteCompany);

  const { visibleColumns, allColumns, isColumnVisible, toggleColumn, resetToDefaults } =
    useColumnVisibility('insurance-companies', COMPANIES_COLUMNS);

  const isLoading = orgLoading || rawCompanies === undefined;

  const filtered = useMemo(() => {
    const list: ICompanyListItem[] = (rawCompanies ?? []) as ICompanyListItem[]; // AS_JUSTIFIED: Convex useQuery returns a structurally compatible type; explicit cast aligns with ICompanyListItem
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(
      (c: ICompanyListItem) =>
        c.name.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q)
    );
  }, [rawCompanies, searchQuery]);

  const companyAccessor = useCallback(
    (company: ICompanyListItem, colId: string): string | number => {
      switch (colId) {
        case 'name':
          return company.name;
        case 'email':
          return company.email ?? '';
        case 'employees':
          return company.employeeCount;
        case 'members':
          return company.memberCount;
        case 'status':
          return company.status;
        case 'created':
          return company.createdAt;
        default:
          return '';
      }
    },
    []
  );
  const {
    sortState,
    toggleSort,
    sortedItems: sortedCompanies,
  } = useTableSort(
    filtered,
    COMPANIES_COLUMNS,
    { defaultSort: { sortBy: 'created', direction: 'desc' } },
    companyAccessor
  );

  const handleArchive = async (e: React.MouseEvent, companyId: Id<'companies'>) => {
    e.stopPropagation();
    try {
      await archiveCompany({ companyId });
      toast.success('Company archived');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to archive company');
    }
  };

  const handleRestore = async (e: React.MouseEvent, companyId: Id<'companies'>) => {
    e.stopPropagation();
    try {
      await restoreCompany({ companyId });
      toast.success('Company restored');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to restore company');
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, companyId: Id<'companies'>, name: string) => {
    e.stopPropagation();
    openDelete(companyId, name);
  };

  const handleDelete = useCallback(
    async (password: string) => {
      if (!deleteTarget) return;
      await deleteCompany({ companyId: deleteTarget.id, password });
      toast.success('Company permanently deleted');
      closeDelete();
    },
    [deleteTarget, deleteCompany, closeDelete]
  );

  if (!orgLoading && !currentOrg) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <Building2 className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">No organisation found</h3>
          <p className="text-sm text-muted-foreground">
            You need to be part of an organisation to manage companies.
          </p>
        </div>
      </div>
    );
  }

  const cellHandlers: CompanyCellHandlers = {
    isOrgOwner,
    onArchive: (e, id) => void handleArchive(e, id),
    onRestore: (e, id) => void handleRestore(e, id),
    onDelete: handleDeleteClick,
  };

  return (
    <TooltipProvider>
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Companies</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {isLoading ? 'Loading...' : `${rawCompanies?.length ?? 0} total`}
            </p>
          </div>
          {isOrgOwner && (
            <Button onClick={openInvite}>
              <Plus aria-hidden="true" className="w-4 h-4 mr-2" />
              Add Company
            </Button>
          )}
        </div>

        <div className="bg-table-container border border-table-container-border rounded-none overflow-hidden">
          <div className="flex items-center gap-3 px-6 py-3 border-b border-table-row-border">
            <label htmlFor="company-search" className="sr-only">
              Search companies
            </label>
            <Input
              id="company-search"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="max-w-xs"
            />
            <div className="ml-auto">
              <ColumnSettingsPopover
                allColumns={allColumns}
                isColumnVisible={isColumnVisible}
                toggleColumn={toggleColumn}
                resetToDefaults={resetToDefaults}
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            {/* Table header */}
            <div className="flex items-center min-w-max px-6 py-2 border-b border-table-row-border bg-muted/30">
              {visibleColumns.map(col => (
                <div
                  key={col.id}
                  className={cn(
                    'shrink-0',
                    col.width,
                    col.pinned === 'left' && 'sticky left-0 z-10 bg-muted/30',
                    col.pinned === 'right' && 'sticky right-0 z-10 bg-muted/30 text-right'
                  )}
                >
                  <SortableColumnHeader
                    column={col}
                    sortState={sortState}
                    onToggleSort={toggleSort}
                  />
                </div>
              ))}
            </div>

            <div className="overflow-auto">
              {isLoading && <CompanyTableSkeleton />}

              {!isLoading &&
                sortedCompanies.map((company: ICompanyListItem) => (
                  <div
                    key={company._id}
                    className={cn(
                      'flex items-center min-w-max px-6 py-3 border-b border-table-row-border hover:bg-muted/50 cursor-pointer',
                      company.status === 'archived' && 'opacity-60'
                    )}
                    onClick={() => navigate(`/insurance/companies/${company._id}`)}
                  >
                    {visibleColumns.map(col => (
                      <span
                        key={col.id}
                        className={cn(
                          'shrink-0 text-sm pr-3',
                          col.width,
                          col.pinned === 'left' && 'sticky left-0 z-10 bg-background',
                          col.pinned === 'right' && 'sticky right-0 z-10 bg-background'
                        )}
                      >
                        {renderCompanyCell(col.id, company, cellHandlers)}
                      </span>
                    ))}
                  </div>
                ))}

              {!isLoading && filtered.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Building2 className="w-12 h-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-1">No companies found</h3>
                  <p className="text-sm text-muted-foreground">
                    {searchQuery
                      ? 'Try adjusting your search query'
                      : 'Add your first company to get started'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {currentOrg && (
          <InviteCompanyDialog
            open={isInviteOpen}
            onOpenChange={setIsInviteOpen}
            orgId={currentOrg._id}
          />
        )}

        <DeleteCompanyDialog
          companyName={deleteTarget?.name ?? ''}
          isOpen={deleteTarget !== null}
          onClose={closeDelete}
          onConfirm={handleDelete}
        />
      </div>
    </TooltipProvider>
  );
}
